import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Waves, Fish, UtensilsCrossed, ArrowRight, Smartphone, Wine, Clock } from "lucide-react";
import { useI18n } from "../i18n";
import { api, fileUrl } from "../lib/api";
import HelmHero from "../components/HelmHero";
import SunsetSpecials from "../components/SunsetSpecials";
import PhotoGallery from "../components/PhotoGallery";
import CountUp from "../components/CountUp";
import AutoVideo from "../components/AutoVideo";
import EventCard from "../components/EventCard";
import Typewriter from "../components/Typewriter";
import useSeo from "../lib/useSeo";

const SUNSET_BG = fileUrl("/api/files/eltimon/site/29713dc5-fdb6-4ef0-9d2c-e29db76daaec.jpg");
const SUNSET_SPECIAL_BG = "https://images.unsplash.com/photo-1619861906267-8341b4f7c9fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const GALLERY_IMAGES = [
  "/gallery/p01.webp",
  "/gallery/p02.webp",
  "/gallery/p03.webp",
  "/gallery/p04.webp",
  "/gallery/p05.webp",
  "/gallery/p06.webp",
  "/gallery/p07.webp",
  "/gallery/p08.webp",
  "/gallery/p09.webp",
  "/gallery/p10.webp",
  "/gallery/p11.webp",
  "/gallery/p12.webp",
  "/gallery/p13.webp",
  "/gallery/p14.webp",
  "/gallery/p15.webp",
  "/gallery/p16.webp",
  "/gallery/p17.webp",
  "/gallery/p18.webp",
  "/gallery/feat1.webp",
  "/gallery/feat2.webp",
];

export default function Home() {
  const { lang, t } = useI18n();
  const [menu, setMenu] = useState([]);
  const [events, setEvents] = useState([]);
  const [promo, setPromo] = useState(null);
  const [settings, setSettings] = useState({ currency: "C$" });

  useSeo({
    title: lang === "es"
      ? "Restaurante El Timón San Juan del Sur | Mariscos frente a la playa desde 1979"
      : "El Timón Restaurant San Juan del Sur | Beachfront Seafood Since 1979",
    description: lang === "es"
      ? "El mejor restaurante de mariscos en San Juan del Sur. 46 años de tradición frente a la playa: pescado, langosta, ceviche y camarones frescos. Ordena en línea."
      : "The best seafood restaurant in San Juan del Sur. 46 years of beachfront tradition: fresh fish, lobster, ceviche and shrimp. Order online.",
  });

  useEffect(() => {
    api.get("/menu").then((r) => setMenu(r.data.filter((m) => m.featured).slice(0, 6))).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/promo").then((r) => setPromo(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <main>
      <HelmHero />

      {/* Promo banner (image or reel video) — above heritage */}
      {promo && promo.enabled && promo.image && (
        <section className="bg-[#0A192F] py-14 px-6 flex justify-center" data-testid="home-promo-section">
          <div className="max-w-5xl w-full mx-auto flex flex-col items-center">
            {(() => {
              const isVid = /\.(mp4|mov|webm|m4v)$/i.test(promo.image);
              const caption = (promo.text_es || promo.text_en) && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/70 to-transparent p-6 sm:p-8 pointer-events-none">
                  <p className="text-white font-display text-xl sm:text-3xl leading-tight">{lang === "es" ? promo.text_es : promo.text_en}</p>
                </div>
              );
              const inner = isVid ? (
                <div data-testid="home-promo-banner" className="mx-auto rounded-2xl overflow-hidden border border-amber-500/25 shadow-2xl relative bg-black flex justify-center">
                  <video src={promo.image} autoPlay muted loop playsInline className="max-h-[80vh] w-auto max-w-full mx-auto block" />
                  {caption}
                </div>
              ) : (
                <div data-testid="home-promo-banner" className="mx-auto w-full rounded-2xl overflow-hidden border border-amber-500/25 shadow-2xl relative">
                  <img src={promo.image} alt={lang === "es" ? (promo.text_es || "Promoción El Timón") : (promo.text_en || "El Timón promo")} className="w-full max-h-[440px] object-cover object-center block mx-auto" />
                  {caption}
                </div>
              );
              return promo.link
                ? <a href={promo.link} target="_blank" rel="noreferrer" className="block w-full">{inner}</a>
                : inner;
            })()}
          </div>
        </section>
      )}

      {/* Heritage strip */}
      <section className="bg-[#112240] py-20 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="fly-left">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-amber-400 mb-4">{t.common.since}</p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-white leading-tight">{t.home.heritageTitle}</h2>
            <p className="text-slate-300 mt-5 leading-relaxed">{t.home.heritageText}</p>
            <div className="grid grid-cols-3 gap-4 mt-8">
              {[{ end: 46, suffix: "", fmt: false, l: t.home.stat1 }, { end: 1424, suffix: "", fmt: true, l: t.home.stat2 }, { end: 100, suffix: "%", fmt: false, l: t.home.stat3 }].map((s, i) => (
                <div key={i} className="bg-[#0A192F] rounded-xl p-4 text-center border border-amber-500/15">
                  <div className="font-display text-2xl lg:text-3xl text-amber-400"><CountUp end={s.end} suffix={s.suffix} format={s.fmt} /></div>
                  <div className="text-[11px] text-slate-400 mt-1 leading-tight">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative fly-right">
            <AutoVideo src="/since1979.mp4" poster="/since1979.jpg" className="rounded-2xl shadow-2xl w-full h-[380px] object-cover" />
            <div className="absolute -bottom-5 -left-5 bg-amber-500 text-slate-950 rounded-2xl px-5 py-4 shadow-xl hidden sm:block">
              <div className="font-display font-bold text-2xl leading-none">1,424</div>
              <div className="text-[11px] font-semibold mt-1">{t.home.stat2}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#0A192F] py-16 px-6">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-3 gap-6 fly-left">
          {[{ icon: Fish, ...t.about.values[0] }, { icon: Waves, ...t.about.values[1] }, { icon: UtensilsCrossed, ...t.about.values[2] }].map((v, i) => (
            <div key={i} className="value-card reveal bg-[#112240]/90 border border-slate-700/60 rounded-2xl p-6 text-center flex flex-col items-center hover:border-amber-500/40 transition-colors">
              <v.icon className="h-8 w-8 text-amber-400" />
              <h3 className="font-display text-xl text-white mt-4">{v.t}</h3>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Sunset Specials (rotating) */}
      <SunsetSpecials items={menu} bg={SUNSET_SPECIAL_BG} currency={settings.currency} />

      {/* Experience */}
      <section className="relative py-28 px-6 overflow-hidden">
        <img src={SUNSET_BG} alt="Sunset San Juan del Sur" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0A192F]/80" />
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-white fly-left">{t.home.experienceTitle}</h2>
          <p className="text-slate-200 mt-5 text-lg leading-relaxed"><Typewriter text={t.home.experienceText} speed={16} /></p>
        </div>
      </section>

      {/* Events */}
      {events.length > 0 && (
        <section className="bg-[#0A192F] py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-amber-400 mb-3">{t.home.eventsSub}</p>
                <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">{t.home.eventsTitle}</h2>
              </div>
              <Link to="/events" className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200">{t.nav.events} <ArrowRight className="h-4 w-4" /></Link>
            </div>
            {(() => {
              const happy = events.find((e) => e.title_en === "Happy Hour");
              const weekend = events.filter((e) => e.title_en !== "Happy Hour");
              return (
                <>
                  {happy && (
                    <div data-testid="home-happy-hour-banner" className="mb-10 rounded-2xl bg-gradient-to-r from-amber-500 to-amber-400 text-slate-950 p-6 sm:p-8 shadow-2xl flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
                      <div className="flex items-center gap-4">
                        <div className="h-14 w-14 rounded-full bg-slate-950/10 flex items-center justify-center shrink-0"><Wine className="h-7 w-7" /></div>
                        <div>
                          <div className="font-display text-2xl sm:text-3xl font-bold leading-none">{lang === "es" ? happy.title_es : happy.title_en}</div>
                          <div className="text-sm font-semibold mt-1 opacity-80">{lang === "es" ? happy.recurring_es : happy.recurring_en}</div>
                        </div>
                      </div>
                      <div className="sm:ml-auto sm:text-right">
                        <div className="inline-flex items-center gap-2 font-bold text-xl"><Clock className="h-5 w-5" /> {happy.time}</div>
                        <div className="text-sm font-medium mt-1 max-w-sm">{lang === "es" ? happy.desc_es : happy.desc_en}</div>
                      </div>
                    </div>
                  )}
                  <div className="grid md:grid-cols-3 gap-6" data-testid="home-events-grid">
                    {weekend.map((e) => <Link key={e.id} to="/events" data-testid={`home-event-link-${e.id}`} className="block cursor-pointer"><EventCard event={e} /></Link>)}
                  </div>
                </>
              );
            })()}
          </div>
        </section>
      )}

      {/* Gallery */}
      <section className="bg-[#112240] py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-white text-center mb-10 reveal">{t.home.galleryTitle}</h2>
          <PhotoGallery images={GALLERY_IMAGES} />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A192F] py-20 px-6">
        <div className="max-w-3xl mx-auto text-center bg-gradient-to-br from-[#1D2D50] to-[#112240] border border-amber-500/25 rounded-3xl p-12">
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">{t.home.ctaTitle}</h2>
          <p className="text-slate-300 mt-3">{t.home.ctaText}</p>
          <Link to="/menu" data-testid="home-cta-order" className="cta-glow inline-flex items-center gap-2 mt-7 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-8 py-3.5 rounded-full transition-all hover:scale-[1.03]"><Smartphone className="h-4 w-4" /> {t.common.orderNow}</Link>
        </div>
      </section>
    </main>
  );
}
